import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Remembers a customer's queue ticket on their own phone so a page refresh (or closing
 *  and reopening the tab) keeps them on their place in line instead of a blank join form.
 *  Polls the live position every 10s, and clears the saved ticket on a definite
 *  "ticket not found" (e.g. the next trading day) so they get a fresh form. */
export function useQueueTicket(store, enabled = true) {
  const key = `dj_queue_ticket_${store}`;
  const todayLocal = () =>
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Australia/Melbourne",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date());
  const [ticket, setTicketState] = useState(() => {
    if (!enabled) return null;
    try {
      const s = localStorage.getItem(key);
      const t = s ? JSON.parse(s) : null;
      // Ticket numbers restart from 1 every trading day, so a ticket saved on a previous day
      // must never be shown today — it could clash with today's same number. Drop it so the
      // customer gets a fresh join form.
      if (t && t.doc_date && t.doc_date !== todayLocal()) {
        localStorage.removeItem(key);
        return null;
      }
      return t;
    } catch {
      return null;
    }
  });
  const [live, setLive] = useState(null);

  const setTicket = useCallback(
    (t) => {
      setTicketState(t);
      try {
        if (t) localStorage.setItem(key, JSON.stringify(t));
        else localStorage.removeItem(key);
      } catch {
        /* storage unavailable (e.g. private mode) — ticket just won't survive a refresh */
      }
      if (!t) setLive(null);
    },
    [key]
  );

  const resetTicket = useCallback(() => setTicket(null), [setTicket]);

  const leaveQueue = useCallback(async () => {
    try {
      if (ticket?.id) await axios.post(`${API}/public/queue/${store}/leave`, { id: ticket.id });
    } catch {
      /* even if the server call fails, free this phone so the customer isn't stuck */
    }
    setTicket(null);
  }, [ticket, store, setTicket]);

  useEffect(() => {
    if (!enabled || !ticket?.ticket) return undefined;
    let active = true;
    const poll = () =>
      axios
        .get(`${API}/public/queue/${store}/position/${ticket.ticket}`, {
          params: ticket.id ? { id: ticket.id } : {},
        })
        .then(({ data }) => {
          if (!active) return;
          // Left from another tab/device — free this phone too, don't show "You're next".
          if (data?.status === "left") {
            try {
              localStorage.removeItem(key);
            } catch {
              /* ignore */
            }
            setTicketState(null);
            setLive(null);
            return;
          }
          setLive(data);
        })
        .catch((err) => {
          // A definite "ticket not found" (new trading day, or ticket removed) clears the
          // saved ticket so the customer sees a fresh join form. Transient/network errors
          // are ignored so a brief blip never loses their number.
          if (active && err?.response?.status === 404) {
            try {
              localStorage.removeItem(key);
            } catch {
              /* ignore */
            }
            setTicketState(null);
            setLive(null);
          }
        });
    poll();
    const timer = setInterval(poll, 10000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [enabled, ticket, store, key]);

  return { ticket, live, setTicket, resetTicket, leaveQueue };
}
